import React from "react";
import { shuffleGameData } from "../../lib/game-helpers";
import GameGrid from "../GameGrid";
import NumberOfMistakesDisplay from "../NumberOfMistakesDisplay";
import TryAgainModal from "../modals/TryAgainModal";

import { Separator } from "../ui/separator";
import ConfettiExplosion from "react-confetti-explosion";

import { PuzzleDataContext } from "../../providers/PuzzleDataProvider";
import { GameStatusContext } from "../../providers/GameStatusProvider";
import GameControlButtonsPanel from "../GameControlButtonsPanel";
import ViewSurpriseModal from "../modals/ViewSurpriseModal";
import { Button } from "../ui/button";
import {
  hasSeenSurprise,
  markSurpriseSeen,
  clearSurpriseSeen,
  clearSurpriseAnswer,
} from "../../lib/local-storage";

function Game() {
  const { gameData, categorySize, numCategories } =
    React.useContext(PuzzleDataContext);
  const {
    solvedGameData,
    isGameOver,
    isGameWon,
    isOutOfMistakes,
    resetGame,
  } = React.useContext(GameStatusContext);

  const [shuffledRows, setShuffledRows] = React.useState(
    shuffleGameData({ gameData })
  );
  const [isEndGameModalOpen, setisEndGameModalOpen] = React.useState(false);
  const [isTryAgainModalOpen, setIsTryAgainModalOpen] = React.useState(false);
  const [gridShake, setGridShake] = React.useState(false);
  const [showConfetti, setShowConfetti] = React.useState(false);

  // use effect to update Game Grid after a row has been correctly solved
  React.useEffect(() => {
    const categoriesToRemoveFromRows = solvedGameData.map(
      (data) => data.category
    );
    const dataLeftForRows = gameData.filter((data) => {
      return !categoriesToRemoveFromRows.includes(data.category);
    });
    if (dataLeftForRows.length > 0) {
      setShuffledRows(shuffleGameData({ gameData: dataLeftForRows }));
    }
  }, [solvedGameData]);

  // Handle End Game!
  React.useEffect(() => {
    if (!isGameOver) {
      return;
    }
    // Only auto-open + show confetti if the player hasn't already seen the surprise
    if (isGameWon && hasSeenSurprise()) {
      return;
    }
    // extra delay for game won to allow confetti to show
    const modalDelay = isGameWon ? 2000 : 250;
    const delayModalOpen = window.setTimeout(() => {
      setisEndGameModalOpen(true);
      if (isGameWon) {
        markSurpriseSeen();
      }
      //unmount confetti after modal opens
      setShowConfetti(false);
    }, modalDelay);

    if (isGameWon) {
      setShowConfetti(true);
    }

    return () => window.clearTimeout(delayModalOpen);
  }, [isGameOver]);

  // Open Try Again modal when the player runs out of mistakes
  React.useEffect(() => {
    if (isOutOfMistakes) {
      setIsTryAgainModalOpen(true);
    } else {
      setIsTryAgainModalOpen(false);
    }
  }, [isOutOfMistakes]);

  return (
    <>
      <div className={`game-wrapper`}>
        {isTryAgainModalOpen && <TryAgainModal open={isTryAgainModalOpen} />}
        <GameGrid
          gameRows={shuffledRows}
          shouldGridShake={gridShake}
          setShouldGridShake={setGridShake}
        />
        {showConfetti && isGameOver && (
          <div className="grid place-content-center">
            <ConfettiExplosion
              particleCount={100}
              force={0.8}
              duration={2500}
            />
          </div>
        )}
        <Separator />

        {!isGameOver ? (
          <>
            <NumberOfMistakesDisplay />
            <GameControlButtonsPanel
              shuffledRows={shuffledRows}
              setShuffledRows={setShuffledRows}
              setGridShake={setGridShake}
            />
          </>
        ) : (
          <div className="flex flex-wrap gap-4 justify-center">
            <ViewSurpriseModal open={isEndGameModalOpen} />
            <Button
              onClick={() => {
                setisEndGameModalOpen(false);
                clearSurpriseSeen();
                clearSurpriseAnswer();
                resetGame();
              }}
              className="h-10 px-4 rounded-full border border-black bg-white text-black text-sm font-semibold whitespace-nowrap leading-none hover:bg-neutral-100"
            >
              Replay
            </Button>
          </div>
        )}
      </div>
    </>
  );
}

export default Game;
